import { useId } from 'react';
import { RotateCcw, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { resolveAlertHeroStyle } from '@/lib/alertHeroStyle';
import type { AccountAlert } from '@/services/accountAlerts.service';

interface AlertHeroTileProps {
  alert: AccountAlert;
  /** "Sep 27, 2026" style meta line, rendered above the title. */
  dateLabel?: string | null;
  /** Opens the full announcement card. */
  onView: () => void;
  onDismiss?: () => void;
  dismissDisabled?: boolean;
  /** True once the viewer closed it — the dismiss control becomes a restore. */
  dismissed?: boolean;
  /** Status pill for the announcements archive, drawn in the tile's own colors. */
  statusLabel?: string;
}

/**
 * An announcement summarised as a fixed-size tile: the date and title on the same
 * background, gradient or photo and scrim as AlertHeroCard (both resolve through
 * resolveAlertHeroStyle), and one standard "View details" button that opens the
 * full card. The copy, photo, video and call to action all live on that card.
 *
 * The width comes from the archive's grid; the height is a fixed 272px so every
 * row is even. It is set by the tallest content a tile can hold: a four-line
 * title with the touch-sized close control and button (44px each on a phone),
 * 260px. Titles clamp at four lines to keep inside it at any width; the full
 * card shows the rest.
 */
export function AlertHeroTile({
  alert,
  dateLabel,
  onView,
  onDismiss,
  dismissDisabled = false,
  dismissed = false,
  statusLabel,
}: AlertHeroTileProps) {
  const style = resolveAlertHeroStyle(alert);
  // Every tile's button reads "View details", so its accessible name adds the title,
  // taken from the rendered heading so entities in the stored markup decode.
  const titleId = useId();
  const viewId = useId();

  return (
    <article
      className="relative flex h-[17rem] min-w-0 flex-col gap-4 overflow-hidden rounded-xl p-5 shadow-sm"
      style={style.container}
    >
      <div className="min-w-0">
        <div className="flex items-start gap-3">
          {dateLabel && (
            <p
              className="pt-0.5 text-[11px] font-medium uppercase tracking-wide"
              style={{ color: style.mutedTextColor }}
            >
              {dateLabel}
            </p>
          )}

          {(statusLabel || onDismiss) && (
            <div className="ml-auto flex shrink-0 items-center gap-2">
              {statusLabel && (
                <span
                  className="rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                  style={{ color: style.mutedTextColor, borderColor: style.borderColor }}
                >
                  {statusLabel}
                </span>
              )}

              {onDismiss && (
                <button
                  type="button"
                  onClick={onDismiss}
                  disabled={dismissDisabled}
                  className="rounded-md p-0.5 opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60 disabled:opacity-40 touch-target-44 tap-feedback"
                  style={{ color: style.textColor }}
                  aria-label={dismissed ? 'Restore announcement' : 'Close announcement'}
                  title={dismissed ? 'Restore this announcement' : 'Close this announcement'}
                >
                  {dismissed ? (
                    <RotateCcw className="h-4 w-4 touch-target-icon" />
                  ) : (
                    <X className="h-4 w-4 touch-target-icon" />
                  )}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Inline markup (<b>/<i>/<u>/colour spans) authored in the corporate
            dashboard's title editor, sanitized on write. Not a link here, so the
            tile has a single action; the full card links it. */}
        <h3
          id={titleId}
          className="mt-1 line-clamp-4 break-words text-xl font-semibold tracking-tight"
          style={style.title}
          dangerouslySetInnerHTML={{ __html: alert.alertTitle }}
        />
      </div>

      {/* Pinned to the bottom, so the buttons line up across a row. */}
      <div className="mt-auto flex justify-center">
        <Button
          id={viewId}
          variant="secondary"
          size="sm"
          onClick={onView}
          aria-labelledby={`${viewId} ${titleId}`}
        >
          View details
        </Button>
      </div>
    </article>
  );
}
